import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { FileText, Search, Download, Shield, CheckCircle2, Clock, ExternalLink, X, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COAsPage = () => {
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingCoa, setViewingCoa] = useState(null); // COA being viewed in modal

  useEffect(() => {
    const fetchCOAs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/coas`);
        if (response.ok) {
          setCoas(await response.json());
        }
      } catch (error) {
        console.error('Error fetching COAs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCOAs();
  }, []);

  const getFileUrl = (url) => {
    if (!url) return '';
    // Relative backend paths (uploads or persistent object storage) get the
    // backend origin prefixed; absolute external URLs pass through unchanged.
    return url.startsWith('/') ? `${API_URL}${url}` : url;
  };

  const filteredCOAs = coas.filter(coa =>
    coa.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (coa.batch_number && coa.batch_number.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const availableCOAs = filteredCOAs.filter(c => c.has_coa);
  const pendingCOAs = filteredCOAs.filter(c => !c.has_coa);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Certificates of Analysis (COAs)"
        description="Verify the purity and quality of Genora Peptides research compounds. Access third-party lab certificates for all products."
        keywords="certificate of analysis, COA, peptide purity, HPLC analysis, lab testing, peptide quality"
      />
      {/* COA Viewer Modal */}
      {viewingCoa && viewingCoa.file_url && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-900">
                  Certificate of Analysis
                </h2>
                <p className="text-sm text-slate-500">
                  {viewingCoa.product_name} • Batch: {viewingCoa.batch_number}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={getFileUrl(viewingCoa.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 flex items-center gap-1"
                  title="Open in New Tab"
                >
                  <ExternalLink className="h-5 w-5" />
                </a>
                <button
                  onClick={() => setViewingCoa(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4 bg-slate-100 min-h-[400px] flex items-center justify-center">
              {viewingCoa.file_type === 'image' ? (
                <img 
                  src={getFileUrl(viewingCoa.file_url)} 
                  alt={`COA for ${viewingCoa.product_name}`}
                  className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
                />
              ) : (
                <iframe
                  src={getFileUrl(viewingCoa.file_url)}
                  title={`COA for ${viewingCoa.product_name}`}
                  className="w-full h-[60vh] rounded-lg border-0 bg-white"
                />
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 flex items-center justify-between flex-shrink-0 bg-slate-50">
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span><strong>Purity:</strong> {viewingCoa.purity}</span>
                <span><strong>Test Date:</strong> {viewingCoa.test_date}</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={getFileUrl(viewingCoa.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open in New Tab
                  </Button>
                </a>
                <Button 
                  variant="outline" 
                  onClick={() => setViewingCoa(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center">
              <FileText className="h-6 w-6 text-teal-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Certificates of Analysis
              </h1>
              <p className="text-slate-600">Verify the purity and quality of our research peptides</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { 
              title: 'Third-Party Verified', 
              desc: 'All COAs are from independent laboratories',
              icon: Shield
            },
            { 
              title: 'HPLC Analysis', 
              desc: 'Purity verified via chromatography',
              icon: CheckCircle2
            },
            { 
              title: 'Batch Tracking', 
              desc: 'Full traceability for every product',
              icon: FileText
            },
          ].map((item, index) => (
            <Card key={index} className="bg-white border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-5 w-5 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-slate-100">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Products</p>
              <p className="text-2xl font-heading font-bold text-slate-900">{coas.length}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">COAs Available</p>
              <p className="text-2xl font-heading font-bold text-green-600">{coas.filter(c => c.has_coa).length}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Pending Upload</p>
              <p className="text-2xl font-heading font-bold text-amber-600">{coas.filter(c => !c.has_coa).length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search by product name or batch number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="coa-search"
          />
        </div>

        {/* Available COAs */}
        <Card className="bg-white border-slate-100 mb-8">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <CardTitle>Available Certificates ({availableCOAs.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
                ))}
              </div>
            ) : availableCOAs.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No certificates uploaded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Product</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Batch Number</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Purity</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Test Date</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableCOAs.map((coa) => (
                      <tr key={coa.coa_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`coa-${coa.product_slug}`}>
                        <td className="py-4 px-4">
                          <Link to={`/products/${coa.product_slug}`} className="font-medium text-slate-900 hover:text-teal-700">
                            {coa.product_name}
                          </Link>
                        </td>
                        <td className="py-4 px-4">
                          <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono">
                            {coa.batch_number}
                          </code>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className="bg-green-50 text-green-700 border-green-200">
                            {coa.purity}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-500">
                          {coa.test_date}
                        </td>
                        <td className="py-4 px-4 text-right">
                          {coa.file_url ? (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setViewingCoa(coa)}
                              data-testid={`view-coa-${coa.product_slug}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View {coa.file_type === 'image' ? 'Image' : 'PDF'}
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" disabled>
                              <FileText className="h-4 w-4 mr-1" />
                              No File
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending COAs */}
        <Card className="bg-white border-slate-100">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              <CardTitle>Pending Certificates ({pendingCOAs.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-16 bg-slate-100 rounded animate-pulse"></div>
            ) : pendingCOAs.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-3" />
                <p className="text-slate-500">All products have certificates uploaded!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {pendingCOAs.map((coa) => (
                  <Link 
                    key={coa.coa_id} 
                    to={`/products/${coa.product_slug}`}
                    className="flex items-center justify-between p-3 bg-amber-50/50 border border-amber-100 rounded-lg hover:bg-amber-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-slate-700">{coa.product_name}</span>
                    <ExternalLink className="h-4 w-4 text-amber-600" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-4">
            <Shield className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-amber-800 mb-2">Quality Assurance Statement</h3>
              <p className="text-sm text-amber-700">
                All Certificates of Analysis are generated by independent, third-party laboratories. 
                Each batch is tested for purity, identity, and quality before release. COAs are provided 
                for research documentation purposes. Products are intended for laboratory research use only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default COAsPage;
